import type { ButtonHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName, type IconTone } from './Icon';
import './ui.css';

type Variant = 'plain' | 'soft';

interface CommonProps {
  icon: IconName;
  /**
   * The action in words. Becomes the accessible name *and* the native tooltip,
   * so an operator who does not recognise the glyph can still find out what it
   * does without clicking it.
   */
  label: string;
  tone?: IconTone;
  variant?: Variant;
}

const classNames = (
  tone: IconTone,
  variant: Variant,
  extra?: string,
): string => {
  const classes = ['ui-icon-button'];
  if (variant === 'soft') {
    classes.push('ui-icon-button--soft');
  }
  if (tone !== 'default') {
    classes.push(`ui-icon-button--${tone}`);
  }
  if (extra) {
    classes.push(extra);
  }
  return classes.join(' ');
};

interface IconButtonProps
  extends CommonProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label' | 'title'> {}

/** Icon-only button for dense row and toolbar actions. */
export function IconButton({
  icon,
  label,
  tone = 'default',
  variant = 'soft',
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      className={classNames(tone, variant, className)}
      aria-label={label}
      title={label}>
      <Icon name={icon} size="sm" />
    </button>
  );
}

interface IconLinkProps extends CommonProps {
  to: string;
  /** Router state, e.g. the list query string a detail screen should return to. */
  state?: unknown;
  className?: string;
}

/**
 * Link counterpart, for actions that are really navigation (view details, open
 * a sub-module). A real anchor keeps middle-click and "open in new tab" working
 * where a button would swallow them.
 */
export function IconLink({
  icon,
  label,
  to,
  state,
  tone = 'default',
  variant = 'soft',
  className,
}: IconLinkProps) {
  return (
    <Link
      to={to}
      state={state}
      className={classNames(tone, variant, className)}
      aria-label={label}
      title={label}>
      <Icon name={icon} size="sm" />
    </Link>
  );
}
